import { Schema } from 'effect';
import { SchemaUtils } from '@listening/api-contract/schema-utils';
import { rootLogger } from '@/lib/root-logger';
import { callApiPromise } from '@/runtimes/get-runtime';
import { cookieTrackersPurchase } from '@/seo/cookie-trackers-purchase';
import {
  arrivedFromStripe,
  NO_PURCHASE_PIXELS,
  purchasePixelsFrom,
  type PurchasePixelFields,
} from './post-purchase-pixels';

const moduleLogger = rootLogger.child({ module: 'PurchaseReturn' });

export const purchaseReturnSearchFields = {
  payment_intent: Schema.optional(Schema.String),
  setup_intent: Schema.optional(Schema.String),
  redirect_status: Schema.optional(
    SchemaUtils.LiteralOrUndefined('succeeded', 'failed', 'pending'),
  ),
};

type PurchaseReturnDeps = {
  readonly paymentIntent?: string | undefined;
  readonly setupIntent?: string | undefined;
  readonly redirectStatus?: 'succeeded' | 'failed' | 'pending' | undefined;
};

export const purchaseReturnDeps = (search: {
  readonly payment_intent?: string | undefined;
  readonly setup_intent?: string | undefined;
  readonly redirect_status?: 'succeeded' | 'failed' | 'pending' | undefined;
}) => ({
  paymentIntent: search.payment_intent,
  setupIntent: search.setup_intent,
  redirectStatus: search.redirect_status,
});

type PurchaseReturn = PurchasePixelFields & {
  readonly stripeIntentId: string | null;
};

export const NO_PURCHASE_RETURN: PurchaseReturn = {
  ...NO_PURCHASE_PIXELS,
  stripeIntentId: null,
};

export const confirmPurchaseReturn = async (
  deps: PurchaseReturnDeps,
  signal: AbortSignal,
) => {
  const pixels = arrivedFromStripe(deps)
    ? await callApiPromise((api) => api.Billing.Confirm(), { signal })
        .then(purchasePixelsFrom)
        .catch((error: unknown) => {
          moduleLogger.error(
            { error, deps },
            'Billing.Confirm loader call failed — skipping conversion + trial pixels',
          );
          return NO_PURCHASE_PIXELS;
        })
    : NO_PURCHASE_PIXELS;
  return {
    ...pixels,
    stripeIntentId: deps.setupIntent ?? deps.paymentIntent ?? null,
  };
};

export const purchaseReturnScripts = (pixels: PurchaseReturn | undefined) =>
  cookieTrackersPurchase({
    amountUsd: pixels?.amountUsd ?? null,
    currency: pixels?.currency ?? null,
    trialStarted: pixels?.trialStarted ?? false,
    stripeIntentId: pixels?.stripeIntentId ?? null,
    purchaseEventId: pixels?.purchaseEventId ?? null,
  });
